"use strict";
// Anthropic tool definition + system prompt for EMR extraction.
//
// Two things live here together because they evolve in lockstep:
//   1. EMIT_PATIENT_INFO_TOOL — the JSON Schema we hand to Claude via
//      tool-use. Forces structural conformance (right shape, right types).
//   2. SYSTEM_PROMPT — the rules the model must follow when filling that
//      schema. Developed and tuned via manual claude.ai testing on
//      Kantime + Axxess HomeCare fixtures.
//
// Keep them in one file so they're updated as a unit. If we later add
// more LLM features (summarization etc.), THOSE get their own tool +
// prompt pair under their own subdirectories.
Object.defineProperty(exports, "__esModule", { value: true });
exports.SYSTEM_PROMPT = exports.EMIT_PATIENT_INFO_TOOL = void 0;
exports.buildUserMessage = buildUserMessage;
const CONTACT_ENTRY_SCHEMA = {
    type: "object",
    properties: {
        name: { type: "string" },
        phoneNo: { type: "string" },
        countryCode: { type: "string", enum: ["+1", "+91", "+63"] },
        sortOrder: { type: "integer", enum: [1, 2] },
    },
    required: ["name", "phoneNo", "countryCode", "sortOrder"],
    additionalProperties: false,
};
exports.EMIT_PATIENT_INFO_TOOL = {
    name: "emit_patient_info",
    description: "Emit the structured patient information extracted from the EMR paste. Call this tool exactly once with the extracted data. Do not call it more than once.",
    input_schema: {
        type: "object",
        properties: {
            mrn: {
                type: "string",
                description: 'The patient\'s MRN / Medical Record Number / Patient ID, exactly as it appears in the source (alphanumeric). Empty string "" if not present.',
            },
            patientInfo: {
                type: "object",
                properties: {
                    firstName: { type: "string" },
                    lastName: { type: "string" },
                    phoneNo: {
                        type: "string",
                        description: "Digits only, no formatting. Empty string if not present.",
                    },
                    address: {
                        type: "object",
                        properties: {
                            placeName: {
                                type: "string",
                                description: "Full address as a single line — combine street, city, state, zip if split across lines.",
                            },
                            apartmentNo: { type: "string" },
                        },
                        required: ["placeName", "apartmentNo"],
                        additionalProperties: false,
                    },
                    email: { type: "string" },
                    dob: {
                        type: "string",
                        description: 'Date of birth in "MM/DD/YYYY" format. Empty string if not present.',
                    },
                },
                required: [
                    "firstName",
                    "lastName",
                    "phoneNo",
                    "address",
                    "email",
                    "dob",
                ],
                additionalProperties: false,
            },
            insurance: { type: "string" },
            about: {
                type: "string",
                description: "Free-text clinical notes and alerts. May paraphrase or combine notes sections.",
            },
            info: {
                type: "object",
                properties: {
                    physician: {
                        type: "array",
                        items: CONTACT_ENTRY_SCHEMA,
                        minItems: 2,
                        maxItems: 2,
                        description: "Exactly 2 slots — sortOrder 1 (primary) and 2 (secondary).",
                    },
                    pharmacy: {
                        type: "array",
                        items: CONTACT_ENTRY_SCHEMA,
                        minItems: 1,
                        maxItems: 1,
                        description: "Exactly 1 slot — sortOrder 1.",
                    },
                    careGiver: {
                        type: "array",
                        items: CONTACT_ENTRY_SCHEMA,
                        minItems: 2,
                        maxItems: 2,
                        description: "Exactly 2 slots — sortOrder 1 (primary) and 2 (secondary).",
                    },
                },
                required: ["physician", "pharmacy", "careGiver"],
                additionalProperties: false,
            },
        },
        required: ["mrn", "patientInfo", "insurance", "about", "info"],
        additionalProperties: false,
    },
};
// The system prompt. EMR-agnostic — the EMR-type hint goes in the user
// message ("The EMR is KANTIME" etc.) since different EMRs share these
// rules but differ in paste shape.
//
// Rules 1-11 were developed by manual claude.ai testing against Kantime
// and Axxess HomeCare fixtures. The numbering matches the prompt history
// in ai_plans/emr-parser-llm-spike.md.
exports.SYSTEM_PROMPT = `You are an EMR text extraction system. The user will paste text copied from an EMR patient profile page. Extract the structured patient information by calling the emit_patient_info tool exactly once.

CRITICAL RULES — read carefully, these are non-negotiable:

1. NO HALLUCINATIONS. Every string value you emit (except "about" and "insurance") MUST appear as a substring of the source text. Do NOT invent or paraphrase names, phone numbers, addresses, dates, or emails. If you cannot find a value in the source, return "" for that field. The output will be programmatically validated for this — invented values will be rejected.

2. PHONE NUMBERS. Extract digits only, no parentheses, hyphens, or spaces. Example: source "(720) 297-8419" → output "7202978419".

3. DOB FORMAT. Convert whatever format is in the source to "MM/DD/YYYY". Example: source "12/20/1977" → output "12/20/1977".

4. ARRAY SHAPES. The physician, careGiver, and pharmacy arrays must have the exact slot counts shown in the schema: physician 2 slots, careGiver 2 slots, pharmacy 1 slot. Fill empty slots with { "name": "", "phoneNo": "", "countryCode": "+1", "sortOrder": <N> }.

5. PATIENT NAME FLIP. If the patient's name appears in the source as "LAST, FIRST" (common in some EMRs), split on the comma — last name before, first name after. Output firstName and lastName separately.

6. ADDRESS COMBINE. For "address.placeName", combine street, city, state, and zip if they are split across lines, separated by ", ". Do NOT include UI labels like "Lat / Long updated with Address" even if they appear nearby.

7. CAREGIVER RELATIONSHIP PACKING. If the source provides a caregiver/emergency-contact relationship label (e.g., "Brother", "Spouse", "Mother", "Daughter"), pack it into the caregiver name in parentheses: "John Smith (Brother)". If no relationship is identifiable, return just the name: "John Smith". The "(Relationship)" suffix is an explicit allowed exception to rule 1.

8. PHYSICIAN NAME PRESERVATION. Physician names: keep as they appear in the source (often "Last, First M.D." or "Dr. First Last"). Do not reformat — just trim leading/trailing whitespace.

9. PRIMARY vs SECONDARY. For careGiver entries, the contact marked "Primary" goes to sortOrder 1; the one marked "Secondary" goes to sortOrder 2. If only one contact exists, the second slot stays empty.

10. NAME-FORMAT VERBATIM EXCEPTION. For physician, pharmacy, and careGiver names, if the source uses "LAST, FIRST" or "LAST, FIRST MIDDLE" format, flip to "First Last" or "First Middle Last". This is an explicit exception to rule 1.

11. AUXILIARY PHONE NUMBERS. If a contact's section contains multiple phone numbers (typical pattern: main + fax), use only the first number as phoneNo. Discard fax / alt-phone — the schema has no slot for them.

12. MRN / PATIENT ID. Extract the patient's MRN (also labeled "Medical Record Number", "Patient ID", "Patient Id", "MR#", or "Chart #") verbatim as it appears — alphanumeric, no reformatting. This is subject to rule 1 (must appear in the source). If no such identifier is present, return "".

When you are unsure about a value, return "" for that field. Do NOT guess. Returning "" is always safer than inventing data.`;
// Builds the user message. The EMR-type hint helps the model anchor its
// expectations about paste structure (Kantime = section-marker tables;
// Axxess HomeCare = line-positional; etc.). The model figures out the
// specific structure from the source text itself.
function buildUserMessage(pastedText, emrType) {
    return `The EMR is ${emrType}.

Source text follows. Extract the patient information by calling emit_patient_info.

---

${pastedText}`;
}
